import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { createProvisionalConversationAgent } from '@atlassian/conversation-assistant-store/controllers/chat/conversation-agent-identity';
import {
	CHAT_STORE_STATUS,
	StoreActionLoggableName,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { clearStatus } from './clearStatus';
import { createSafeAction, type SafeAction } from './createSafeAction';
import { extractAgentVersionFromMessages } from './extractAgentVersionFromMessages';
import { initializeConversationData } from './initializeConversationData';
import { normalizeRestorableRequestedMode } from './normalizeRestorableRequestedMode';
import { processConversationMessages } from './processConversationMessages';
import { preserveTrailingFollowUps } from './preserveTrailingFollowUps';
import { setConversationData } from './setConversationData';
import { shouldPreprocessDetailsForConversation } from './shouldPreprocessDetailsForConversation';
import type { ChatStoreAction, WarmConversationCacheArgs } from './types';

export const warmConversationCache: SafeAction<[WarmConversationCacheArgs]> = createSafeAction(
	StoreActionLoggableName.warmConversationCache,
	({
		conversation: conversationParams,
		forceRefresh,
		onSuccess,
		onError,
	}: WarmConversationCacheArgs): ChatStoreAction =>
		async (
			{ getState, dispatch },
			{
				assistanceService,
				experienceTracker,
				sendAnalyticsEvent,
				getMessages,
				setMessages,
				capabilities,
			},
		) => {
			const conversationId = conversationParams.id;

			const existingConversation = getState().conversations[conversationId];

			// Skip if conversation is actively in-flight
			if (
				existingConversation?.status === CHAT_STORE_STATUS.loadingChat ||
				existingConversation?.status === CHAT_STORE_STATUS.awaitingResponse ||
				existingConversation?.status === CHAT_STORE_STATUS.typingResponse
			) {
				return;
			}

			// Skip if conversation is already loaded (idle with messages) unless forceRefresh
			if (existingConversation?.status === CHAT_STORE_STATUS.idle && !forceRefresh) {
				return;
			}

			try {
				experienceTracker.start({
					name: ExperienceName.WARM_CONVERSATION_CACHE,
					id: conversationId,
					attributes: {
						conversationId,
						forceRefresh: Boolean(forceRefresh),
					},
				});

				// Bootstrap conversation in store with provided metadata or client defaults.
				const conversationAgentForWarmLoadingReset = isExperimentEnabled('rovo_chat_mention_agents')
					? (existingConversation?.agent ??
						createProvisionalConversationAgent(
							conversationParams.agentId ?? existingConversation?.sourceAgentId,
						))
					: undefined;
				const conversationAgentFieldsForWarmLoadingReset = conversationAgentForWarmLoadingReset
					? { agent: conversationAgentForWarmLoadingReset }
					: {};

				const messagesBeforeReload = getMessages(conversationId);

				dispatch(
					initializeConversationData({
						conversationId,
						data: {
							id: conversationId,
							name: conversationParams.name ?? existingConversation?.name ?? '',
							timeCreated:
								conversationParams.timeCreated ??
								existingConversation?.timeCreated ??
								new Date().toISOString(),
							status: CHAT_STORE_STATUS.loadingChat,
							traces: [],
							sourceAgentId:
								conversationParams.agentId ?? existingConversation?.sourceAgentId ?? '',
							...conversationAgentFieldsForWarmLoadingReset,
							messages: [],
						},
					}),
				);

				// If metadata params not fully provided, fetch from API in parallel (silent failure)
				const hasFullMetadata =
					conversationParams.name != null && conversationParams.timeCreated != null;

				const metadataPromise: Promise<void> = hasFullMetadata
					? Promise.resolve()
					: assistanceService
							.fetchConversationMetadata(conversationId)
							.then((metadata) => {
								const fetchedConversationAgentFields =
									metadata.agent && isExperimentEnabled('rovo_chat_mention_agents')
										? { agent: metadata.agent }
										: {};

								dispatch(
									setConversationData({
										conversationId,
										data: {
											name: metadata.name ?? conversationParams.name ?? '',
											timeCreated:
												metadata.time_created ??
												conversationParams.timeCreated ??
												existingConversation?.timeCreated ??
												new Date().toISOString(),
											sourceAgentId:
												fg('jira_ai_task_level_planning_ootb_agent') ||
												fg('jira-ai-fix-refine-in-chat-with-ootb-agent')
													? (metadata.agent?.agent_id ??
														metadata.agent?.agent_named_id ??
														conversationParams.agentId ??
														existingConversation?.sourceAgentId ??
														'')
													: (metadata.agent?.agent_named_id ??
														conversationParams.agentId ??
														existingConversation?.sourceAgentId ??
														''),
											...fetchedConversationAgentFields,
											...(metadata.tags !== undefined ? { tags: metadata.tags } : {}),
											lastRequestedMode: normalizeRestorableRequestedMode(
												metadata.last_requested_mode,
											),
										},
									}),
								);
							})
							.catch(() => {
								// Silent failure — keep client defaults
							});

				// Run messages fetch and metadata fetch in parallel; wait for both
				const chatPromise = assistanceService.getChat(conversationId, capabilities);

				const [chatResult] = await Promise.allSettled([chatPromise, metadataPromise]);

				if (chatResult.status === 'rejected') {
					const normalizedError =
						chatResult.reason instanceof Error
							? chatResult.reason
							: new Error(String(chatResult.reason));
					experienceTracker.fail({
						name: ExperienceName.WARM_CONVERSATION_CACHE,
						error: normalizedError,
					});
					dispatch(
						setConversationData({
							conversationId,
							data: clearStatus({}),
						}),
					);
					onError?.(normalizedError);
					return;
				}

				const serviceChat = chatResult.value;

				const status = getState().conversations[conversationId]?.status;

				if (status !== CHAT_STORE_STATUS.loadingChat) {
					return;
				}

				const messages = processConversationMessages({
					latestMessages: serviceChat,
					conversationId,
					sendAnalyticsEvent,
					shouldPreprocessDetails: shouldPreprocessDetailsForConversation(
						getState(),
						conversationId,
					),
				});

				const fetchedAgentVersion = extractAgentVersionFromMessages(serviceChat);

				dispatch(
					setConversationData({
						conversationId,
						data: {
							...clearStatus({ lastAgentMessageStatus: 'SUCCESSFUL' }),
							...(fetchedAgentVersion ? { agentVersion: fetchedAgentVersion } : {}),
						},
					}),
				);

				setMessages(
					conversationId,
					fg('rovo_chat_preserve_follow_ups_on_refetch')
						? preserveTrailingFollowUps(messagesBeforeReload, messages)
						: messages,
				);

				experienceTracker.succeed({
					name: ExperienceName.WARM_CONVERSATION_CACHE,
				});

				onSuccess?.();
			} catch (e) {
				const normalizedError = e instanceof Error ? e : new Error(String(e));
				experienceTracker.fail({
					name: ExperienceName.WARM_CONVERSATION_CACHE,
					error: normalizedError,
				});
				onError?.(normalizedError);
			}
		},
	{
		experienceName: () => ExperienceName.WARM_CONVERSATION_CACHE,
		actionErrorHandler: () => {
			// Suppress all default error handling for cache warming
			return;
		},
	},
);
